import React, { useState } from "react";
import moment from "moment";
// v1 date range picker, by design choice.
import {
  colors2,
  DateRangePicker,
  LoaderOverlay,
  UI_CAPTION_SEMIBOLD,
} from "oolib";
import {
  ButtonPrimary,
  ButtonTertiary,
} from "oolib/dist/v2/components/Buttons";
import { useExternalConnectionStatus } from "@/utils/react-query-hooks/externalConnections";
import { IntegrationDisconnectConfirmModal } from "../IntegrationDisconnectConfirmModal";
import { IntegrationModalShell } from "../comps/IntegrationModalShell";
import { ConnectionCard } from "../comps/ConnectionCard";
import { EditImportSettings } from "../comps/EditImportSettings";
import { ManageIntegrationModal } from "../comps/ManageIntegrationModal";
import { caption, isoDaysAgo } from "../comps/common";

/**
 * Fathom's popups, on IntegrationModalShell.
 *
 * ImportModal is "Import from Fathom": the connected account and a date range.
 * It is the import wizard's Fathom step (DirectDataImport renders it for a
 * connected Fathom): Continue hands the adapter options to onContinue, and the
 * wizard fetches and moves on to field mapping, passing back isLoading.
 *
 * ManageModal is the shared manage popup, with a date range to import more.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;

// Same default as the old Fathom step: the last year.
const initialRange = () => [isoDaysAgo(365), isoDaysAgo(0)];

// Why a range can't be imported (error), or a size warning, as the old Fathom
// step had them.
const checkRange = ([startDate, endDate]) => {
  if (!startDate || !endDate) return { error: "Pick a start and end date." };
  if (new Date(startDate) > new Date(endDate))
    return { error: "End date must be on or after start date." };
  const days = Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS);
  if (days > HARD_CAP_DAYS)
    return {
      error: `Please select a range of ${HARD_CAP_DAYS} days or fewer. Run multiple imports for longer history.`,
    };
  if (days > SOFT_WARN_DAYS)
    return {
      warn: `Large date ranges (${days} days) may take several minutes to import.`,
    };
  return {};
};

// The picked local days as UTC instants: start of the start day, end of the
// end day (e.g. 2025-05-03 00:00 IST -> 2025-05-02T18:30:00.000Z).
const toAdapterOptions = ([startDate, endDate]) => ({
  createdAfter: moment(startDate).startOf("day").toISOString(),
  createdBefore: moment(endDate).endOf("day").toISOString(),
});

// Date range and its notes, shared by the import popup and "Edit import
// settings".
const FathomFields = ({ dateRange, setDateRange, readOnly }) => {
  const { error, warn } = checkRange(dateRange);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <DateRangePicker
        id="dateRange"
        label="Date range"
        value={dateRange}
        onChange={(_k, v) => setDateRange(v)}
        previousDateOnly
        readOnly={readOnly}
      />
      {error && (
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
          {error}
        </UI_CAPTION_SEMIBOLD>
      )}
      {warn && caption(warn)}
      {caption(
        "Large imports (more than ~25 meetings) may time out, so try a shorter range first if you have lots of meetings.",
      )}
    </div>
  );
};

function ImportModal({
  Icon,
  onClose,
  onBack,
  onContinue,
  isLoading,
  hideBackdrop,
  fromManage,
  initialOptions,
}) {
  const { data: conn } = useExternalConnectionStatus("fathom");
  // initialOptions: a range already picked elsewhere (and being fetched),
  // shown here so a failed fetch can be adjusted and retried.
  const [dateRange, setDateRange] = useState(() =>
    initialOptions
      ? [
          moment(initialOptions.createdAfter).format("YYYY-MM-DD"),
          moment(initialOptions.createdBefore).format("YYYY-MM-DD"),
        ]
      : initialRange(),
  );
  const [showDisconnect, setShowDisconnect] = useState(false);
  const canContinue = !checkRange(dateRange).error;

  return (
    <IntegrationModalShell
      title="Import from Fathom"
      Icon={Icon}
      onBack={onBack}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      footer={
        <>
          <ButtonTertiary onClick={onClose}>
            {fromManage ? "Discard" : "Do Later"}
          </ButtonTertiary>
          <ButtonPrimary
            onClick={() => onContinue(toAdapterOptions(dateRange))}
            disabled={!canContinue || isLoading}
            isLoading={isLoading}
          >
            Continue
          </ButtonPrimary>
        </>
      }
    >
      {isLoading && (
        <LoaderOverlay loaderText="Fetching data from external source..." />
      )}
      {/* Left out when launched from the manage popup, which shows it. */}
      {!fromManage && (
        <ConnectionCard
          account={conn?.providerAccountName || "Fathom"}
          email={conn?.adminEmail}
          disconnectDisabled={isLoading}
          onDisconnect={() => setShowDisconnect(true)}
        />
      )}
      {showDisconnect && (
        <IntegrationDisconnectConfirmModal
          provider="fathom"
          name="Fathom"
          onClose={() => setShowDisconnect(false)}
          // Nothing to import from once disconnected.
          onDisconnected={onClose}
        />
      )}
      {caption(
        "Import Fathom meetings into your dataset. Each meeting becomes a row, with its transcript as speaker-prefixed text.",
      )}
      <FathomFields
        dateRange={dateRange}
        setDateRange={setDateRange}
        readOnly={isLoading}
      />
      {caption(
        "Fathom may occasionally miss portions of a meeting transcript due to limitations on their side. Review transcripts for completeness before relying on them.",
      )}
    </IntegrationModalShell>
  );
}

// "Edit import settings": pick a range to import more meetings into this
// dataset. Save runs it through the import wizard, which fetches straight
// away, maps fields and appends.
const EditFathomImport = ({ onSave }) => {
  const [dateRange, setDateRange] = useState(initialRange);
  return (
    <EditImportSettings
      saveDisabled={!!checkRange(dateRange).error}
      onSave={() => onSave(toAdapterOptions(dateRange))}
    >
      <FathomFields dateRange={dateRange} setDateRange={setDateRange} />
    </EditImportSettings>
  );
};

// The shared manage popup, with Fathom's "Edit import settings".
const ManageModal = (props) => (
  <ManageIntegrationModal
    provider="fathom"
    EditImport={EditFathomImport}
    {...props}
  />
);

export const fathom = {
  ImportModal,
  ManageModal,
};
